import React, { useState, useEffect } from 'react';
import { Heart, MapPin, Star, Award, Crown, Medal, Calendar, Users, Wifi, Car, Coffee, Waves, Mountain, Building, TreePine, Search, Filter, ChevronDown, Sparkles, Shield, CheckCircle, Calculator, Globe, Gift, Camera, Zap, ExternalLink, Phone, Mail, Home, ArrowRight, BarChart3, Target, Clock, Lightbulb, Brain, TrendingUp, Smartphone, Video, FileCheck, MousePointer, Key, Database, Cpu, Euro, Banknote, Scale, UserCheck, Quote } from 'lucide-react';
import ClaraTop10Modal from '../components/ClaraTop10Modal';

const HeartPlacesComplete = () => {
  const [selectedDays, setSelectedDays] = useState(30);
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedMode, setSelectedMode] = useState('buy');
  const [darkMode, setDarkMode] = useState(false);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [claraModalOpen, setClaraModalOpen] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState('family');
  
  // New state for rental calculator
  const [rentInputs, setRentInputs] = useState({
    destination: 'mallorca',
    usageDays: 14,
    guests: 2,
    season: 'high'
  });
  
  // New state for technical evolution
  const [activePhase, setActivePhase] = useState('digital-now');

  // Simulate AI analysis when days change
  useEffect(() => {
    if (selectedDays > 0) {
      setAiLoading(true);
      const timer = setTimeout(() => {
        setShowAIInsights(true);
        setAiLoading(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [selectedDays]);

  // AI-generated insights based on investment days
  const getAIInsights = () => {
    if (selectedDays <= 14) {
      return {
        recommendation: "Short-term optimal",
        riskLevel: "Low",
        marketTiming: "Excellent entry point",
        aiScore: 92,
        insights: [
          "Peak season availability in Mallorca",
          "15% above average ROI expected",
          "Low market volatility detected"
        ]
      };
    } else if (selectedDays <= 60) {
      return {
        recommendation: "Balanced investment",
        riskLevel: "Medium",
        marketTiming: "Good timing",
        aiScore: 87,
        insights: [
          "Seasonal optimization possible",
          "Portfolio diversification recommended",
          "Strong rental demand forecasted"
        ]
      };
    } else {
      return {
        recommendation: "Long-term strategy",
        riskLevel: "Medium",
        marketTiming: "Strategic positioning",
        aiScore: 94,
        insights: [
          "Maximum ROI potential identified",
          "Market expansion opportunities",
          "Premium property appreciation expected"
        ]
      };
    }
  };

  const aiInsights = getAIInsights();

  const openClaraModal = (persona = 'family') => {
    setSelectedPersona(persona);
    setClaraModalOpen(true);
  };

  // NEW: Navigate to portfolio page instead of modal
  const navigateToPortfolio = () => {
    window.location.href = '/portfolio';
  };

  // NEW: Rental calculation logic
  const calculateRentMetrics = () => {
    const { usageDays, guests, season } = rentInputs;
    
    let dailyRate = 180; // Base rate
    
    const seasonMultipliers = {
      low: 0.8,
      mid: 1.0,
      high: 1.4,
      peak: 1.8
    };
    
    const guestMultiplier = guests > 2 ? 1 + ((guests - 2) * 0.15) : 1;
    const adjustedRate = dailyRate * seasonMultipliers[season] * guestMultiplier;
    const totalCost = adjustedRate * usageDays;
    
    const cleaningFee = 120;
    const serviceFee = totalCost * 0.12;
    const totalWithFees = totalCost + cleaningFee + serviceFee;
    
    const hotelRate = season === 'peak' ? 220 : season === 'high' ? 180 : season === 'mid' ? 140 : 110;
    const hotelTotal = hotelRate * usageDays;
    const savings = hotelTotal - totalWithFees;
    const savingsPercentage = (savings / hotelTotal) * 100;
    
    const costPerPersonPerDay = totalWithFees / (usageDays * guests);
    
    return {
      dailyRate: adjustedRate,
      totalCost,
      cleaningFee,
      serviceFee,
      totalWithFees,
      hotelTotal,
      savings: Math.max(savings, 0),
      savingsPercentage: Math.max(savingsPercentage, 0),
      costPerPersonPerDay
    };
  };

  const rentMetrics = calculateRentMetrics();

  // NEW: AI Insights for rental
  const getRentalAIInsights = () => {
    const { savingsPercentage, costPerPersonPerDay } = rentMetrics;
    
    if (savingsPercentage > 25) {
      return {
        recommendation: "Ausgezeichneter Wert",
        color: "text-green-600",
        insights: [
          `${savingsPercentage.toFixed(0)}% Ersparnis gegenüber Hotels`,
          `Nur €${costPerPersonPerDay.toFixed(0)} pro Person/Tag`,
          "Premium-Ausstattung inklusive Küche und Wohnbereich"
        ]
      };
    } else if (savingsPercentage > 10) {
      return {
        recommendation: "Gutes Preis-Leistungs-Verhältnis",
        color: "text-blue-600",
        insights: [
          `${savingsPercentage.toFixed(0)}% günstiger als vergleichbare Hotels`,
          "Mehr Platz und Privatsphäre",
          "Flexible Selbstversorgung möglich"
        ]
      };
    } else {
      return {
        recommendation: "Premium-Erlebnis",
        color: "text-purple-600",
        insights: [
          "Luxuriöse Ausstattung rechtfertigt Premiumpreis",
          "Einzigartige Immobilie mit besonderen Features",
          "Investment in unvergessliche Erlebnisse"
        ]
      };
    }
  };

  const rentalAiInsights = getRentalAIInsights();

  // NEW: Technical evolution phases
  const evolutionPhases = {
    'digital-now': {
      title: "Jetzt - Digital First Co-Ownership",
      subtitle: "Moderner Start ohne Mittelalter-Bürokratie",
      color: "from-blue-500 to-indigo-600",
      icon: <Smartphone className="w-8 h-8" />,
      description: "Vollständig digitaler Prozess von der Auswahl bis zum Eigentum - schlank, modern, effizient",
      features: [
        {
          icon: <MousePointer className="w-6 h-6" />,
          title: "Online-Konfiguration",
          description: "Wählen Sie Objekt, Anteile und Co-Eigentümer komplett online",
          details: "Wie bei Mein Homes: Objekt auswählen, Anteile konfigurieren, Mitinvestoren finden - alles digital"
        },
        {
          icon: <Video className="w-6 h-6" />,
          title: "Video-Notartermin",
          description: "Ein einziger Video-Call für alle Beteiligten - rechtlich vollgültig",
          details: "Seit 2022 möglich: Alle Co-Eigentümer nehmen remote teil, VideoIdent, elektronische Unterschrift"
        },
        {
          icon: <Database className="w-6 h-6" />,
          title: "Sofortige Digitale Verwaltung",
          description: "Ab Tag 1 läuft alles über die Clara360 Plattform",
          details: "Dashboard, Buchungskalender, automatische Zahlungen - keine Wartezeit"
        },
        {
          icon: <Brain className="w-6 h-6" />,
          title: "KI-Preisoptimierung",
          description: "Clara AI optimiert Mietpreise von Anfang an",
          details: "Keine manuelle Preisfindung - KI analysiert Markt und optimiert kontinuierlich"
        }
      ],
      benefits: [
        "Kompletter Prozess in 2-3 Wochen statt Monaten",
        "Ein einziger Video-Notartermin für alle",
        "Sofortige Renditeoptimierung durch KI",
        "Minimaler Bürokratie-Aufwand"
      ]
    },
    'ai-enhanced': {
      title: "Phase 1 - KI-Enhanced Management",
      subtitle: "2025: Vollautomatisierte Optimierung",
      color: "from-purple-500 to-pink-600",
      icon: <Brain className="w-8 h-8" />,
      description: "KI übernimmt komplette Verwaltung und Optimierung für maximale Renditen",
      features: [
        {
          icon: <Brain className="w-6 h-6" />,
          title: "Predictive Pricing",
          description: "KI prognostiziert optimale Preise 30 Tage im Voraus",
          details: "Berücksichtigt Events, Wetter, Konkurrenz, saisonale Trends - 25% höhere Renditen"
        },
        {
          icon: <Target className="w-6 h-6" />,
          title: "Automated Maintenance",
          description: "IoT-Sensoren erkennen Wartungsbedarf automatisch",
          details: "Präventive Reparaturen, automatische Auftragserteilung, Kostenoptimierung"
        },
        {
          icon: <Users className="w-6 h-6" />,
          title: "Smart Guest Matching",
          description: "KI matcht Gäste optimal zu Objekten",
          details: "Höhere Zufriedenheit, weniger Schäden, bessere Bewertungen"
        },
        {
          icon: <TrendingUp className="w-6 h-6" />,
          title: "Portfolio Intelligence",
          description: "KI-Empfehlungen für weitere Investments",
          details: "Marktanalyse, Diversifikation, Timing-Empfehlungen für Expansion"
        }
      ],
      benefits: [
        "25-40% höhere Renditen durch KI-Optimierung",
        "90% weniger Verwaltungsaufwand",
        "Proaktive Wartung reduziert Kosten um 30%",
        "Datenbasierte Expansion-Strategien"
      ]
    },
    'blockchain': {
      title: "Phase 2 - Blockchain Tokenisierung",
      subtitle: "2026: Maximale Liquidität und Transparenz",
      color: "from-green-500 to-emerald-600",
      icon: <Zap className="w-8 h-8" />,
      description: "Vollständige Tokenisierung für sofortige Liquidität und globalen Handel",
      features: [
        {
          icon: <Key className="w-6 h-6" />,
          title: "Instant Liquidity",
          description: "Anteile jederzeit in Minuten verkaufen",
          details: "24/7 Handel auf TokenBay.com, sofortige Abwicklung, transparente Preise"
        },
        {
          icon: <Globe className="w-6 h-6" />,
          title: "Global Marketplace",
          description: "Weltweiter Zugang zu Investoren",
          details: "Internationale Käufer, höhere Nachfrage, bessere Preise"
        },
        {
          icon: <Shield className="w-6 h-6" />,
          title: "100% Transparency",
          description: "Alle Transaktionen auf der Blockchain",
          details: "Unveränderliche Aufzeichnungen, vollständige Nachvollziehbarkeit"
        },
        {
          icon: <Cpu className="w-6 h-6" />,
          title: "Smart Contract Automation",
          description: "Null menschliche Eingriffe nötig",
          details: "Automatische Ausschüttungen, Abstimmungen, Verwaltung - alles programmiert"
        }
      ],
      benefits: [
        "Sofortige Liquidität - Ein-/Ausstieg in Minuten",
        "Globaler Markt erhöht Objektwerte um 15-25%",
        "Eliminierung aller Mittelsmänner",
        "100% transparente und automatisierte Verwaltung"
      ]
    }
  };

  const currentPhase = evolutionPhases[activePhase];

  const getPhaseStatus = (phase) => {
    const statusMap = {
      'digital-now': 'Verfügbar',
      'ai-enhanced': 'Q2 2025',
      'blockchain': 'Q1 2026'
    };
    return statusMap[phase];
  };

  const getPhaseStatusColor = (phase) => {
    const colorMap = {
      'digital-now': 'bg-green-500',
      'ai-enhanced': 'bg-blue-500',
      'blockchain': 'bg-purple-500'
    };
    return colorMap[phase];
  };

  return (
    <div className="min-h-screen">
      {/* Seamless Background Gradient Container */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to bottom, #ffffff 0%, #fafaf9 10%, #fff8dc 20%, #f5f5f4 30%, #fef3c7 40%, #fdf2f8 50%, #fce7f3 60%, #fdf2f8 70%, #fef3c7 80%, #f5f5f4 90%, #fafaf9 100%)'
        }}></div>
      </div>
      
      {/* Dark Mode Toggle */}
      <button
        onClick={() => setDarkMode(!darkMode)}
        className="fixed bottom-6 right-6 z-40 p-3 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 rounded-full shadow-lg hover:shadow-xl transition-all"
      >
        {darkMode ? '☀️' : '🌙'}
      </button>

      {/* Hero Section - UPDATED COLORS */}
      <div className="relative overflow-hidden bg-gradient-to-br from-stone-100 via-gray-100 to-stone-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800">
        <div className="absolute inset-0 bg-white/10 dark:bg-black/10"></div>
        
        <div className="relative z-10 text-center px-6 max-w-6xl mx-auto py-20">
          <div className="mb-8">
            <div className="inline-flex items-center space-x-2 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-gray-800 dark:text-gray-200 mb-6">
              <Crown className="w-4 h-4" />
              <span>Powered by Clara360 Ecosystem</span>
            </div>
            
            <h1 className="text-6xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-600 via-stone-700 to-gray-800 mb-6">
              HeartPlaces
            </h1>
            
            <p className="text-xl md:text-2xl text-stone-600 dark:text-stone-300 mb-4 font-light">
              Places you lose your heart to
            </p>
            
            <p className="text-lg text-stone-500 dark:text-stone-400 mb-8">
              Der Michelin Guide für Vacation Rentals - Bei uns könnt ihr kaufen UND mieten
            </p>
          </div>
          
          {/* Mode Selection - UPDATED COLORS */}
          <div className="flex justify-center mb-8">
            <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm p-2 rounded-2xl shadow-lg">
              <button
                onClick={() => setSelectedMode('buy')}
                className={`px-8 py-3 rounded-xl font-semibold transition-all ${
                  selectedMode === 'buy'
                    ? 'bg-gray-700 text-white shadow-lg'
                    : 'text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                Kaufen - Co-Ownership
              </button>
              <button
                onClick={() => setSelectedMode('rent')}
                className={`px-8 py-3 rounded-xl font-semibold transition-all ${
                  selectedMode === 'rent'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700'
                }`}
              >
                Mieten - Traumurlaub
              </button>
            </div>
          </div>
          
          {/* Country Selection */}
          <div className="flex justify-center space-x-4 mb-8">
            <button
              onClick={() => setSelectedCountry('all')}
              className={`px-6 py-3 rounded-xl font-medium transition-all ${
                selectedCountry === 'all'
                  ? 'bg-white dark:bg-slate-800 shadow-lg text-gray-900 dark:text-white'
                  : 'bg-white/60 dark:bg-slate-900/60 hover:bg-white/80 dark:hover:bg-gray-700/80'
              }`}
            >
              🌍 All Countries
            </button>
            <button
              onClick={() => setSelectedCountry('spain')}
              className={`px-6 py-3 rounded-xl font-medium transition-all ${
                selectedCountry === 'spain'
                  ? 'bg-white dark:bg-slate-800 shadow-lg text-gray-900 dark:text-white'
                  : 'bg-white/60 dark:bg-slate-900/60 hover:bg-white/80 dark:hover:bg-gray-700/80'
              }`}
            >
              🇪🇸 Spain
            </button>
            <button
              onClick={() => setSelectedCountry('germany')}
              className={`px-6 py-3 rounded-xl font-medium transition-all ${
                selectedCountry === 'germany'
                  ? 'bg-white dark:bg-slate-800 shadow-lg text-gray-900 dark:text-white'
                  : 'bg-white/60 dark:bg-slate-900/60 hover:bg-white/80 dark:hover:bg-gray-700/80'
              }`}
            >
              🇩🇪 Germany
            </button>
          </div>

          {/* Clara AI Button */}
          {selectedMode === 'rent' && (
            <div className="flex justify-center mb-8">
              <button
                onClick={() => openClaraModal('family')}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl font-semibold transition-colors inline-flex items-center space-x-2 shadow-lg"
              >
                <Sparkles className="w-5 h-5" />
                <span>Clara's Top 10 entdecken</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 🆕 3-SLIDE STORY INTEGRATION */}
      {/* Story Slide 1: Die Sehnsucht */}
      <div className="relative py-20 overflow-hidden">
        {/* Soft gradient background */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to bottom, #fafaf9 0%, #f5f5f4 20%, #fafaf9 40%, #f5f5f4 60%, #fafaf9 80%, #fafaf9 100%)'
        }}></div>
        
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full text-sm font-medium text-stone-600 mb-8">
              <Heart className="w-4 h-4 text-rose-500" />
              <span>Die Sehnsucht</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold text-stone-800 mb-8">
              Die Welt ruft
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <blockquote className="text-2xl md:text-3xl text-stone-600 italic mb-8 leading-relaxed">
                <Quote className="w-8 h-8 text-stone-400 inline-block mr-2" />
                "Not all those who wander are lost"
                <footer className="text-lg text-stone-500 mt-4">— J.R.R. Tolkien</footer>
              </blockquote>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-stone-800 mb-4">
                  Seit Jahrhunderten träumen wir
                </h3>
                <p className="text-stone-600 text-lg leading-relaxed">
                  Die Sehnsucht nach fremden Orten schlummert tief in uns. Von den ersten Entdeckern bis zu den digitalen Nomaden von heute - der Drang, neue Welten zu erkunden, prägt die Menschheit.
                </p>
              </div>
              
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 shadow-lg">
                <div className="flex items-center space-x-4 mb-4">
                  <Globe className="w-8 h-8 text-blue-500" />
                  <h3 className="text-2xl font-bold text-stone-800">
                    Die Welt ist ein Buch
                  </h3>
                </div>
                <p className="text-stone-600 text-lg leading-relaxed">
                  "Wer nie reist, sieht nur eine Seite davon." Diese Worte des heiligen Augustinus sind heute aktueller denn je. Jeder Ort erzählt seine eigene Geschichte.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl p-8 shadow-lg">
                <div className="text-center">
                  <div className="text-4xl font-bold text-blue-600 mb-2">2.3 Mrd</div>
                  <div className="text-stone-600">Reisende weltweit</div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-3xl p-8 shadow-lg">
                <div className="text-center">
                  <div className="text-4xl font-bold text-green-600 mb-2">47%</div>
                  <div className="text-stone-600">Träumen vom eigenen Feriendomizil</div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-3xl p-8 shadow-lg">
                <div className="text-center">
                  <div className="text-4xl font-bold text-purple-600 mb-2">∞</div>
                  <div className="text-stone-600">Erinnerungen an besondere Orte</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Story Slide 2: Die Herausforderung (Climax) */}
      <div className="relative py-20 overflow-hidden">
        {/* Cooler gradient for the challenge */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to bottom, #fafaf9 0%, #f5f5f4 15%, #e7e5e4 30%, #d6d3d1 45%, #e7e5e4 60%, #f5f5f4 75%, #fafaf9 90%, #fafaf9 100%)'
        }}></div>
        
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full text-sm font-medium text-stone-600 mb-8">
              <Sparkles className="w-4 h-4 text-stone-500" />
              <span>Technologie-Revolution</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold text-stone-800 mb-8">
              Aber die Realität...
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Brain className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-stone-800 mb-4">Bequemlichkeit</h3>
              <p className="text-stone-600">vs.</p>
              <h3 className="text-xl font-bold text-blue-600 mt-2">Potenzial</h3>
              <p className="text-stone-500 mt-4 text-sm">
                Millionen Traumimmobilien stehen 300+ Tage im Jahr leer
              </p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-stone-800 mb-4">Besitzdenken</h3>
              <p className="text-stone-600">vs.</p>
              <h3 className="text-xl font-bold text-green-600 mt-2">Sharing</h3>
              <p className="text-stone-500 mt-4 text-sm">
                "Das gehört nur mir" blockiert gemeinsame Träume
              </p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-400 to-gray-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Cpu className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-stone-800 mb-4">Technologie-Lücken</h3>
              <p className="text-stone-600">vs.</p>
              <h3 className="text-xl font-bold text-purple-600 mt-2">IoT-Zukunft</h3>
              <p className="text-stone-500 mt-4 text-sm">
                Veraltete Systeme hemmen moderne Lösungen
              </p>
            </div>
          </div>

          <div className="text-center">
            <div className="bg-gradient-to-br from-stone-100 to-stone-200 rounded-3xl p-12 shadow-xl">
              <h3 className="text-3xl font-bold text-stone-800 mb-6">
                Der Wendepunkt
              </h3>
              <blockquote className="text-xl text-stone-600 italic leading-relaxed">
                "Was wäre, wenn wir nicht nur Gäste wären, sondern Gastgeber werden könnten? 
                Was wäre, wenn Technologie Vertrauen schafft statt Überwachung? 
                Was wäre, wenn Teilen bedeutet, mehr zu besitzen?"
              </blockquote>
            </div>
          </div>
        </div>
      </div>

      {/* Story Slide 3: Die HeartPlaces Revolution */}
      <div className="relative py-20 overflow-hidden">
        {/* Warm, harmonious gradient for the solution */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to bottom, #f5f5f4 0%, #fafaf9 15%, #fafaf9 30%, #f5f5f4 45%, #fafaf9 60%, #fafaf9 75%, #f5f5f4 90%, #fafaf9 100%)'
        }}></div>
        
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full text-sm font-medium text-stone-600 mb-8">
              <Heart className="w-4 h-4 text-rose-500" />
              <span>HeartPlaces</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold text-stone-800 mb-8">
              HeartPlaces
            </h2>
            
            <p className="text-2xl text-stone-600 max-w-3xl mx-auto leading-relaxed">
              Wo Träume auf Technologie treffen und Sharing zu echtem Besitz wird
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16">
            <div className="space-y-8">
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-stone-800">Token-basierte Governance</h3>
                </div>
                <p className="text-stone-600 leading-relaxed">
                  Blockchain-gestützte Verwaltung schafft Transparenz ohne Überwachung. 
                  Dezentrale Eigentumsstrukturen ermöglichen echte Mitbestimmung.
                </p>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-stone-800">Community-Standards</h3>
                </div>
                <p className="text-stone-600 leading-relaxed">
                  Mit Eigentum kommt Verantwortung. Gegenseitige Bewertung und Vertrauen 
                  fördern Exzellenz durch Selbstregulierung.
                </p>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-stone-800">IoT + DSGVO</h3>
                </div>
                <p className="text-stone-600 leading-relaxed">
                  Smarte Ferienhäuser mit weniger Datenerfassung. 
                  Technologie, die Vertrauen schafft, nicht Kontrolle.
                </p>
              </div>
            </div>

            <div className="text-center">
              <div className="bg-gradient-to-br from-stone-100 to-stone-200 rounded-3xl p-12 shadow-xl">
                {/* Haus-Lebenszyklus Timeline */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-stone-800 mb-6 text-center">Digitaler Haus-Lebenszyklus</h3>
                  <div className="flex justify-between items-center mb-8">
                    <div className="text-center flex-1">
                      <Building className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                      <div className="text-sm font-semibold">Erwerb</div>
                      <div className="text-xs text-stone-600">Smart Contract</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-stone-400" />
                    <div className="text-center flex-1">
                      <Home className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <div className="text-sm font-semibold">Setup</div>
                      <div className="text-xs text-stone-600">IoT Integration</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-stone-400" />
                    <div className="text-center flex-1">
                      <Cpu className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                      <div className="text-sm font-semibold">Clara360</div>
                      <div className="text-xs text-stone-600">Verwaltung</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-stone-400" />
                    <div className="text-center flex-1">
                      <Euro className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <div className="text-sm font-semibold">Erträge</div>
                      <div className="text-xs text-stone-600">Optimierung</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-stone-400" />
                    <div className="text-center flex-1">
                      <Target className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                      <div className="text-sm font-semibold">Zukunft</div>
                      <div className="text-xs text-stone-600">Ihre Wahl</div>
                    </div>
                  </div>
                </div>
                
                {/* Clara360 Verwaltung */}
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 text-center">
                  <div className="flex justify-center space-x-6 mb-4">
                    <Wifi className="w-6 h-6 text-blue-600" />
                    <Database className="w-6 h-6 text-green-600" />
                    <Zap className="w-6 h-6 text-yellow-600" />
                    <Shield className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="text-lg font-bold text-stone-800 mb-2">Clara360 verwaltet alles automatisch</div>
                  <div className="text-stone-600 text-sm">Während Sie sich auf wichtige Entscheidungen konzentrieren: Vererben, Verkaufen oder Vermieten</div>
                </div>
              </div>

          <div className="text-center">
            <div className="bg-gradient-to-br from-stone-50 to-stone-100 rounded-3xl p-12 shadow-xl">
              <h3 className="text-4xl font-bold text-stone-800 mb-8">
                Hier beginnt deine Geschichte
              </h3>
              <p className="text-xl text-stone-600 leading-relaxed max-w-3xl mx-auto">
                Nicht mehr nur träumen. Nicht mehr nur mieten. Nicht mehr allein investieren. 
                Sondern gemeinsam besitzen, teilen und profitieren - an den Orten, 
                die dein Herz berühren.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* END 3-SLIDE STORY */}

      {/* NEW SECTION: So funktioniert es - Kaufen */}
      <div className="py-20 bg-gradient-to-br from-gray-100 via-stone-100 to-gray-200 dark:from-gray-750 dark:via-gray-800 dark:to-gray-700">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-gray-700 mb-6">
              <Home className="w-4 h-4" />
              <span>Kaufen Demo</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              So funktioniert es - Kaufen
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Intelligente Co-Ownership für Ihre Traumimmobilie - mit transparenter Finanzierung und Clara-Verwaltung
            </p>
          </div>

          {/* Example Purchase Property */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-6 mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <img 
                src="https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=400&h=300&fit=crop"
                alt="Villa Moderna Premium"
                className="w-20 h-20 rounded-xl object-cover"
              />
              <div>
                <h3 className="text-xl font-bold text-gray-900">Villa Moderna Premium</h3>
                <p className="text-gray-600">Mallorca, Cala d'Or • 4 Schlafzimmer • Pool & Meerblick</p>
                <div className="flex items-center space-x-4 mt-2">
                  <span className="text-2xl font-bold text-green-600">€1.2M</span>
                  <span className="text-sm text-gray-500">Gesamtwert</span>
                </div>
              </div>
            </div>
          </div>

          {/* Purchase Calculator */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Investitions-Rechner</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gewünschter Eigentumsanteil
                  </label>
                  <select className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="12.5">1/8 Anteil (45 Tage/Jahr)</option>
                    <option value="25">1/4 Anteil (91 Tage/Jahr)</option>
                    <option value="50">1/2 Anteil (182 Tage/Jahr)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Finanzierungsart
                  </label>
                  <select className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="cash">Vollfinanzierung</option>
                    <option value="mortgage">Mit Hypothek (70%)</option>
                    <option value="mixed">Gemischt</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nutzungsstrategie
                  </label>
                  <select className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="personal">Nur Eigennutzung</option>
                    <option value="mixed">Eigennutzung + Vermietung</option>
                    <option value="investment">Reine Kapitalanlage</option>
                  </select>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Ihre Investition</h4>
                
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Eigentumsanteil:</span>
                    <span className="font-bold">1/8 (12.5%)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Kaufpreis:</span>
                    <span className="font-bold text-blue-600">€150.000</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Nebenkosten:</span>
                    <span className="font-bold">€12.000</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Clara Setup:</span>
                    <span className="font-bold">€3.000</span>
                  </div>
                  <hr className="border-gray-300" />
                  <div className="flex justify-between text-lg">
                    <span className="font-bold">Gesamtinvestition:</span>
                    <span className="font-bold text-green-600">€165.000</span>
                  </div>
                  
                  <div className="mt-6 p-4 bg-white/70 rounded-lg">
                    <div className="text-sm text-gray-600 mb-2">Erwartete Jahresrendite:</div>
                    <div className="text-2xl font-bold text-green-600">8.5%</div>
                    <div className="text-sm text-gray-500">€14.025/Jahr durch Vermietung</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-semibold transition-colors inline-flex items-center space-x-2">
                <Calculator className="w-5 h-5" />
                <span>Detaillierte Analyse anfordern</span>
              </button>
            </div>
          </div>

          {/* Purchase Process Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">1. Objekt wählen</h3>
              <p className="text-gray-600">
                Aus kuratierten Premium-Immobilien in Top-Lagen auswählen
              </p>
            </div>
            
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileCheck className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">2. Anteil sichern</h3>
              <p className="text-gray-600">
                Eigentumsanteil reservieren und Finanzierung abschließen
              </p>
            </div>
            
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Key className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">3. Genießen</h3>
              <p className="text-gray-600">
                Sofort nutzbar - Clara übernimmt komplette Verwaltung
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 365-Day Investment Calculator - UPDATED COLORS */}
      <div className="py-20 px-6 bg-gradient-to-br from-gray-100 via-stone-100 to-gray-200 dark:from-gray-800 dark:via-gray-750 dark:to-gray-700">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-green-100 dark:bg-green-900 px-4 py-2 rounded-full text-green-800 dark:text-green-200 font-medium mb-6">
              <Calculator className="w-5 h-5" />
              <span className="flex items-center space-x-1">
                <span>Clara AI</span>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              </span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              365-Day Investment Calculator
            </h2>
            
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
              Discover your perfect investment timeline with Clara's AI-powered insights
            </p>
          </div>

          <div className="bg-white/90 dark:bg-slate-900 backdrop-blur-sm rounded-3xl shadow-2xl p-8">
            <div className="mb-8">
              <label className="block text-lg font-semibold text-gray-900 dark:text-white mb-4">
                How many days per year will you use your Heart Place?
              </label>
              
              <div className="relative">
                <input
                  type="range"
                  min="7"
                  max="365"
                  value={selectedDays}
                  onChange={(e) => setSelectedDays(parseInt(e.target.value))}
                  className="w-full h-3 bg-gradient-to-r from-gray-300 to-gray-600 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mt-2">
                  <span>7 days</span>
                  <span className="font-bold text-gray-700 text-lg">{selectedDays} days</span>
                  <span>365 days</span>
                </div>
              </div>
            </div>

            {/* AI Insights Panel */}
            {(showAIInsights || aiLoading) && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-6 border-2 border-green-200 dark:border-green-700">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <Brain className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-green-800 dark:text-green-200">
                    Clara AI Analysis
                  </h3>
                  {aiLoading && (
                    <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </div>

                {!aiLoading && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-1">
                        {aiInsights.aiScore}/100
                      </div>
                      <div className="text-sm text-green-700 dark:text-green-300">AI Score</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-800 dark:text-green-200 mb-1">
                        {aiInsights.recommendation}
                      </div>
                      <div className="text-sm text-green-600 dark:text-green-400">Recommendation</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-800 dark:text-green-200 mb-1">
                        {aiInsights.riskLevel}
                      </div>
                      <div className="text-sm text-green-600 dark:text-green-400">Risk Level</div>
                    </div>
                  </div>
                )}

                {!aiLoading && (
                  <div className="mt-6">
                    <h4 className="font-semibold text-green-800 dark:text-green-200 mb-3">
                      Market Insights:
                    </h4>
                    <div className="space-y-2">
                      {aiInsights.insights.map((insight, index) => (
                        <div key={index} className="flex items-center space-x-2 text-green-700 dark:text-green-300">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm">{insight}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-8 text-center">
              <button 
                onClick={navigateToPortfolio}
                className="bg-gray-700 hover:bg-gray-800 text-white px-8 py-4 rounded-xl font-semibold transition-colors inline-flex items-center space-x-2"
              >
                <ArrowRight className="w-5 h-5" />
                <span>Portfolio entdecken</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Clara360 Ecosystem - UPDATED COLORS */}
      <div className="py-20 bg-white/90 backdrop-blur-sm dark:bg-slate-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              Clara360 Ecosystem
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Ein vollständiges Ökosystem für Ihr Immobilien-Investment mit KI-gestützter Verwaltung
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Calendar className="w-8 h-8" />,
                title: "365 Concrete Days",
                description: "Exakte Planung Ihrer Nutzungstage mit KI-Optimierung für maximale Auslastung",
                color: "from-blue-500 to-blue-600"
              },
              {
                icon: <Shield className="w-8 h-8" />,
                title: "Complete Management",
                description: "Vollständige Verwaltung durch Clara360 - von Reinigung bis Wartung",
                color: "from-green-500 to-green-600"
              },
              {
                icon: <Home className="w-8 h-8" />,
                title: "Smart Home Guide",
                description: "Intelligente Haussteuerung und IoT-Integration für modernen Komfort",
                color: "from-purple-500 to-purple-600"
              },
              {
                icon: <Users className="w-8 h-8" />,
                title: "Community Network",
                description: "Exklusives Netzwerk von Co-Eignern und Premium-Services",
                color: "from-gray-500 to-gray-600"
              },
              {
                icon: <Zap className="w-8 h-8" />,
                title: "Blockchain Integration",
                description: "Sichere, transparente Eigentumsverwaltung auf der Blockchain",
                color: "from-indigo-500 to-indigo-600"
              },
              {
                icon: <Gift className="w-8 h-8" />,
                title: "Digital Keys & Access",
                description: "Nahtloser Zugang durch digitale Schlüssel und Smart-Lock-Technologie",
                color: "from-pink-500 to-pink-600"
              }
            ].map((feature, index) => (
              <div key={index} className="bg-gray-50/90 dark:bg-slate-800 backdrop-blur-sm rounded-2xl p-6 hover:shadow-lg transition-shadow">
                <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center text-white mb-4`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button className="bg-gray-700 dark:bg-white text-white dark:text-gray-900 px-8 py-4 rounded-xl font-semibold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors inline-flex items-center space-x-2">
              <span>Ecosystem erkunden</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* NEW SECTION: So funktioniert es - Mieten */}
      <div className="py-20 bg-gradient-to-br from-gray-100 via-stone-100 to-gray-200 dark:from-gray-750 dark:via-gray-800 dark:to-gray-700">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-gray-700 mb-6">
              <Calendar className="w-4 h-4" />
              <span>Mieten Demo</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              So funktioniert es - Mieten
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Flexible Buchungen für Ihren perfekten Urlaub - mit transparenter Preisgestaltung und Clara-Qualität
            </p>
          </div>

          {/* Example Rental Property */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-6 mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <img 
                src="https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=400&h=300&fit=crop"
                alt="Casa Mediterránea Premium"
                className="w-20 h-20 rounded-xl object-cover"
              />
              <div>
                <h3 className="text-xl font-bold text-gray-900">Casa Mediterránea Premium</h3>
                <div className="flex items-center space-x-1 text-gray-600 mb-1">
                  <MapPin className="w-4 h-4" />
                  <span>Port de Sóller, Mallorca</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                    Clara Score: 94
                  </span>
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                    <span className="text-sm font-medium">9.4</span>
                  </div>
                </div>
              </div>
              <div className="ml-auto text-right">
                <div className="text-2xl font-bold text-gray-900">€180</div>
                <div className="text-sm text-gray-600">pro Nacht (Basis)</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Rental Parameters */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
                <Calendar className="w-6 h-6" />
                <span>Ihre Reise-Parameter</span>
              </h3>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Reiseziel
                  </label>
                  <select 
                    value={rentInputs.destination}
                    onChange={(e) => setRentInputs({...rentInputs, destination: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  >
                    <option value="mallorca">Mallorca, Spanien</option>
                    <option value="tuscany">Toskana, Italien</option>
                    <option value="provence">Provence, Frankreich</option>
                    <option value="algarve">Algarve, Portugal</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Aufenthaltsdauer: {rentInputs.usageDays} Tage
                  </label>
                  <input
                    type="range"
                    min="3"
                    max="30"
                    step="1"
                    value={rentInputs.usageDays}
                    onChange={(e) => setRentInputs({...rentInputs, usageDays: parseInt(e.target.value)})}
                    className="w-full h-3 bg-gradient-to-r from-gray-300 to-gray-600 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>3 Tage</span>
                    <span>30 Tage</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Anzahl Gäste: {rentInputs.guests}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="8"
                    step="1"
                    value={rentInputs.guests}
                    onChange={(e) => setRentInputs({...rentInputs, guests: parseInt(e.target.value)})}
                    className="w-full h-3 bg-gradient-to-r from-gray-300 to-gray-600 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1 Person</span>
                    <span>8 Personen</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Reisezeit
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'low', label: 'Nebensaison', desc: 'Nov-Feb' },
                      { value: 'mid', label: 'Zwischensaison', desc: 'Mär-Mai, Sep-Okt' },
                      { value: 'high', label: 'Hauptsaison', desc: 'Jun-Aug' },
                      { value: 'peak', label: 'Spitzensaison', desc: 'Jul-Aug, Feiertage' }
                    ].map((season) => (
                      <button
                        key={season.value}
                        onClick={() => setRentInputs({...rentInputs, season: season.value})}
                        className={`p-3 rounded-xl text-left transition-all ${
                          rentInputs.season === season.value
                            ? 'bg-gray-700 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <div className="font-medium text-sm">{season.label}</div>
                        <div className="text-xs opacity-75">{season.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Rental Results */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
                <BarChart3 className="w-6 h-6" />
                <span>Kostenaufstellung</span>
              </h3>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-600">Unterkunft ({rentInputs.usageDays} Nächte)</span>
                  <span className="font-semibold">€{rentMetrics.totalCost.toFixed(0)}</span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-600">Reinigungsgebühr</span>
                  <span className="font-semibold">€{rentMetrics.cleaningFee}</span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-600">Service-Gebühr</span>
                  <span className="font-semibold">€{rentMetrics.serviceFee.toFixed(0)}</span>
                </div>
                
                <div className="flex justify-between items-center py-3 bg-gray-50 px-4 rounded-xl">
                  <span className="font-bold text-gray-900">Gesamtkosten</span>
                  <span className="font-bold text-2xl text-gray-900">€{rentMetrics.totalWithFees.toFixed(0)}</span>
                </div>
              </div>

              {/* Value Comparison */}
              <div className="bg-blue-50 p-4 rounded-xl mb-6">
                <h4 className="font-semibold text-blue-800 mb-2">Vergleich mit Hotels</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-blue-700">Hotel-Kosten ({rentInputs.usageDays} Nächte)</span>
                    <span className="font-semibold text-blue-800">€{rentMetrics.hotelTotal}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-700">Ihre Ersparnis</span>
                    <span className="font-bold text-green-600">€{rentMetrics.savings.toFixed(0)} ({rentMetrics.savingsPercentage.toFixed(0)}%)</span>
                  </div>
                </div>
              </div>

              {/* Additional Value */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-green-50 p-4 rounded-xl text-center">
                  <div className="text-2xl font-bold text-green-600">
                    €{rentMetrics.costPerPersonPerDay.toFixed(0)}
                  </div>
                  <div className="text-sm text-green-800">Pro Person/Tag</div>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-xl text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    120m²
                  </div>
                  <div className="text-sm text-purple-800">Wohnfläche</div>
                </div>
              </div>

              {/* AI Insights */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <Brain className="w-4 h-4 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-green-800">Clara KI-Bewertung</h4>
                </div>
                
                <div className={`text-xl font-bold mb-3 ${rentalAiInsights.color}`}>
                  {rentalAiInsights.recommendation}
                </div>
                
                <div className="space-y-2">
                  {rentalAiInsights.insights.map((insight, index) => (
                    <div key={index} className="flex items-center space-x-2 text-green-700">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">{insight}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Bereit für Ihren HeartPlaces Urlaub?
              </h3>
              <p className="text-gray-600 mb-6">
                Entdecken Sie verfügbare Mietobjekte mit Clara-Qualitätsgarantie
              </p>
              <div className="flex justify-center space-x-4">
                <button className="bg-gray-700 hover:bg-gray-800 text-white px-8 py-4 rounded-xl font-semibold transition-colors inline-flex items-center space-x-2">
                  <Calendar className="w-5 h-5" />
                  <span>Jetzt buchen</span>
                </button>
                <button className="border-2 border-gray-300 text-gray-700 hover:border-gray-400 px-8 py-4 rounded-xl font-semibold transition-colors inline-flex items-center space-x-2">
                  <Sparkles className="w-5 h-5" />
                  <span>Mehr Objekte</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* NEW SECTION: So funktioniert es - Technisch */}
      <div className="py-20 bg-gradient-to-br from-stone-100 via-gray-100 to-stone-200 dark:from-gray-700 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-gray-700 mb-6">
              <Wifi className="w-4 h-4" />
              <span>Digital First Approach</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              So funktioniert es - Technisch
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Modernes Co-Ownership ohne Mittelalter-Bürokratie - vollständig digital, schlank und effizient
            </p>
          </div>

          {/* Digital First Banner */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-6 mb-12 text-white text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Smartphone className="w-6 h-6" />
              <h3 className="text-xl font-bold">Digital First - Kein Mittelalter!</h3>
            </div>
            <p className="text-blue-100">
              Wie bei Mein Homes: Kompletter Prozess online, Video-Notartermine, sofortige digitale Verwaltung. 
              Von 0 auf 100 in 2-3 Wochen statt Monaten.
            </p>
          </div>

          {/* Phase Navigation */}
          <div className="flex justify-center mb-12">
            <div className="bg-white/90 backdrop-blur-sm p-2 rounded-2xl shadow-lg">
              {Object.entries(evolutionPhases).map(([key, phase]) => (
                <button
                  key={key}
                  onClick={() => setActivePhase(key)}
                  className={`px-4 py-3 rounded-xl font-semibold transition-all inline-flex items-center space-x-2 text-sm ${
                    activePhase === key
                      ? `bg-gradient-to-r ${phase.color} text-white shadow-lg`
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {phase.icon}
                  <div className="text-left">
                    <div className="font-bold">{phase.title.split(' - ')[1] || phase.title.split(' - ')[0]}</div>
                    <div className="text-xs opacity-75">{getPhaseStatus(key)}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Current Phase Display */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-8 mb-12">
            <div className="text-center mb-8">
              <div className={`w-20 h-20 bg-gradient-to-br ${currentPhase.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                {currentPhase.icon}
              </div>
              
              <div className="flex items-center justify-center space-x-3 mb-4">
                <h3 className="text-3xl font-bold text-gray-900">
                  {currentPhase.title}
                </h3>
                <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${getPhaseStatusColor(activePhase)}`}>
                  {getPhaseStatus(activePhase)}
                </span>
              </div>
              
              <p className="text-lg text-gray-600 mb-2">{currentPhase.subtitle}</p>
              <p className="text-gray-600 max-w-3xl mx-auto">{currentPhase.description}</p>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {currentPhase.features.map((feature, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${currentPhase.color} rounded-xl flex items-center justify-center text-white flex-shrink-0`}>
                      {feature.icon}
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">
                        {feature.title}
                      </h4>
                      <p className="text-gray-600 mb-3">
                        {feature.description}
                      </p>
                      <p className="text-sm text-gray-500 bg-white p-3 rounded-lg">
                        {feature.details}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Benefits */}
            <div className="bg-green-50 rounded-2xl p-6">
              <h4 className="text-lg font-bold text-green-800 mb-4 flex items-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span>Vorteile dieser Phase</span>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {currentPhase.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span className="text-green-800 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Bereit für Digital First Co-Ownership?
              </h3>
              <p className="text-gray-600 mb-6">
                Starten Sie heute mit dem modernsten Co-Ownership-Prozess - schlank, digital, effizient
              </p>
              <div className="flex justify-center space-x-4">
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-semibold transition-colors inline-flex items-center space-x-2">
                  <Smartphone className="w-5 h-5" />
                  <span>Digital First starten</span>
                </button>
                <button className="border-2 border-gray-300 text-gray-700 hover:border-gray-400 px-8 py-4 rounded-xl font-semibold transition-colors inline-flex items-center space-x-2">
                  <Video className="w-5 h-5" />
                  <span>Demo-Call buchen</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* We care. We share. - UPDATED COLORS */}
      <div className="py-20 bg-gradient-to-br from-stone-100 via-rose-50 to-pink-100 dark:from-rose-900/20 dark:via-pink-900/20 dark:to-rose-900/20">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="mb-12">
            <div className="w-20 h-20 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              We care. We share.
            </h2>
            
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-2">
              Community → Individualismus
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/90 dark:bg-slate-900 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-rose-400 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Camera className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Shared Experiences
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Gemeinsame Events, Aktivitäten und Touren mit anderen Heart Places Eigentümern. 
                Erleben Sie unvergessliche Momente in der Gemeinschaft.
              </p>
            </div>

            <div className="bg-white/90 dark:bg-slate-900 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-rose-400 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Caring for Each Other
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Echte Verantwortung und gegenseitige Unterstützung. Unser Community-Netzwerk 
                sorgt für Sicherheit und Vertrauen in jeder Situation.
              </p>
            </div>

            <div className="bg-white/90 dark:bg-slate-900 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-rose-400 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Virtual Community
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Clara-Video verbindet alle Heart Places weltweit. Digitale Gemeinschaft 
                mit echten Beziehungen und geteilten Erfahrungen.
              </p>
            </div>
          </div>

          <div className="mt-12">
            <button className="bg-rose-500 hover:bg-rose-600 text-white px-8 py-4 rounded-xl font-semibold transition-colors inline-flex items-center space-x-2">
              <span>Community entdecken</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Future-Proof Investment - UPDATED COLORS */}
      <div className="py-20 bg-white/90 backdrop-blur-sm dark:bg-slate-900">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="mb-12">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Zap className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Future-Proof Investment
            </h2>
            
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Invest today in tomorrow's smart home revolution
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                phase: "Today",
                title: "Smart Foundation",
                description: "Grundlegende IoT-Integration und digitale Infrastruktur",
                color: "from-blue-400 to-blue-500"
              },
              {
                phase: "Soon",
                title: "AI Integration",
                description: "Clara360 KI-Assistenten für automatisierte Hausverwaltung",
                color: "from-green-400 to-green-500"
              },
              {
                phase: "Future",
                title: "Full Automation",
                description: "Vollständig autonome Smart Homes mit predictive maintenance",
                color: "from-purple-400 to-purple-500"
              },
              {
                phase: "Vision",
                title: "Ecosystem Network",
                description: "Vernetzte Heart Places mit geteilter KI und Ressourcen",
                color: "from-gray-400 to-gray-500"
              }
            ].map((phase, index) => (
              <div key={index} className="relative">
                <div className="bg-gray-50/90 dark:bg-slate-800 backdrop-blur-sm rounded-2xl p-6 h-full">
                  <div className={`w-12 h-12 bg-gradient-to-br ${phase.color} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                    <span className="text-white font-bold text-sm">{index + 1}</span>
                  </div>
                  <div className="text-sm font-bold text-gray-500 dark:text-gray-400 mb-2">
                    {phase.phase}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                    {phase.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {phase.description}
                  </p>
                </div>
                
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-6 h-6 text-gray-400" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12">
            <button className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl font-semibold transition-colors inline-flex items-center space-x-2">
              <span>Roadmap ansehen</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* The Future of Property Trading - FIXED BACKGROUND */}
      <div className="py-20" style={{ backgroundColor: '#f5f5f4' }}>
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-12">
            The Future of Property Trading
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-shadow">
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="w-10 h-10 text-white" />
              </div>
              
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                TokenBay.com
              </h3>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Die erste dezentrale Börse für Immobilien-Token. Handeln Sie Ihre Heart Places 
                Anteile sicher und transparent auf der Blockchain.
              </p>
              
              <div className="space-y-3 mb-8">
                {[
                  "Sichere Blockchain-Technologie",
                  "Transparente Preisfindung",
                  "Sofortige Liquidität",
                  "Globaler Marktplatz"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
              
              <button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-xl font-semibold transition-colors flex items-center justify-center space-x-2">
                <span>TokenBay besuchen</span>
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>

            <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-shadow">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <BarChart3 className="w-10 h-10 text-white" />
              </div>
              
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                TokenStream.com
              </h3>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Echtzeit-Analytics und KI-gestützte Marktanalysen für Immobilien-Token. 
                Treffen Sie datenbasierte Investment-Entscheidungen.
              </p>
              
              <div className="space-y-3 mb-8">
                {[
                  "KI-gestützte Marktanalyse",
                  "Echtzeit-Preisdaten",
                  "Portfolio-Optimierung",
                  "Predictive Analytics"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
              
              <button className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-6 rounded-xl font-semibold transition-colors flex items-center justify-center space-x-2">
                <span>TokenStream besuchen</span>
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="mt-12">
            <button className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-8 py-4 rounded-xl font-semibold transition-colors inline-flex items-center space-x-2">
              <span>Trading Platform entdecken</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Clara Top 10 Modal */}
      <ClaraTop10Modal
        isOpen={claraModalOpen}
        onClose={() => setClaraModalOpen(false)}
        destination={selectedCountry === 'spain' ? 'Mallorca' : selectedCountry === 'germany' ? 'Munich' : 'Mallorca'}
        persona={selectedPersona}
        adults={2}
      />
      </div>    </div>
    </div>
  );
};

export default HeartPlacesComplete;

